import React from "react"
import { ListGroup, ListGroupItem, Container } from "reactstrap"
import { Trans, useLingui } from "@lingui/react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import uuid from "uuid"
import { formatStr } from "../../../view/shared/utilities/outputFormat"
import { getCurrency } from "../../../view/shared/utilities/currency"
import { selectMemberSharesFund } from "../../../store/reducers/getMemberBalance"
import { getMemberBalanceByMemberId } from "../../../store/reducers/transactions/actions"
import { intToArabic } from "../../../view/shared/utilities/arabicNumParser"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons"
import { setCurrentPage } from "../../../store/reducers/navigation"
import styles from "./style.module.css"

const MemberOverview = (props) => {
  const { i18n } = useLingui()
  const memberId = props.currentMember?.pubkey // Always use pubkey as the identifier

  React.useEffect(() => {
    if (!props.currentMember) {
      props.setPage("group-main")
    }
    // Only fetch balance if we have a permanent ID
    if (props.currentMember?.id) {
      props.getBalance(props.currentMember.id)
    }
  }, [props.currentMember])

  const getTotalShares = () => {
    const account = props.memberBalance(memberId)
    if (!account) return 0
    return account.savings.balance / parseFloat(props.group?.cycle?.shareValue)
  }

  return (
    <div>
      <div>
        <h4 className={styles.header}>{props.memberDetails?.full_name}</h4>
      </div>
      <Container>
        <ListGroup
          flush
          style={{
            textAlign: i18n.locale === "ar" ? "right" : "intial",
          }}
        >
          {props.memberBalance(memberId) &&
            Object.values(props.memberBalance(memberId))
              .filter(
                ({ accountType, balance }) =>
                  !["total_loan_interest", "paid_loan_interest"].includes(
                    accountType,
                  ) && balance > 0,
              )
              .sort((a, b) =>
                a.balance > b.balance ? 0 : b.balance > a.balance ? 1 : -1,
              )
              .map((bal) => (
                <ListGroupItem key={uuid.v4()} className={styles.listItem}>
                  {formatStr(txnTypes[i18n.locale][bal.accountType])} :{" "}
                  {i18n.locale === "ar"
                    ? intToArabic(parseFloat(bal.balance).toFixed(2))
                    : parseFloat(bal.balance).toFixed(2)}{" "}
                  {!["ar", "lb"].includes(i18n.locale) &&
                    getCurrency(props.group?.country).symbol}
                  {["ar", "lb"].includes(i18n.locale) &&
                    (props.vsla?.metadata?.currency === "USD"
                      ? getCurrency(props.group?.country).usd_symbol
                      : getCurrency(props.group?.country).lbp_symbol)}
                </ListGroupItem>
              ))}
          {!["ar", "lb"].includes(i18n.locale) && (
            <ListGroupItem className={styles.listItem}>
              <Trans id="shares.in.numbers">Shares in numbers</Trans>
              {"\n"}
              {parseFloat(getTotalShares()).toFixed(2)}
            </ListGroupItem>
          )}
          <ListGroupItem className={styles.listItem}>
            <Trans id="member.role">Member Role</Trans> :{" "}
            {i18n._(
              props.memberDetails?.member_role
                ?.toLocaleLowerCase()
                ?.split(" ")
                ?.join("_"),
            )}
          </ListGroupItem>
          {props.currentMember?.blockchain_address && (
            <ListGroupItem className={styles.listItem}>
              {i18n._("BC Wallet")} :{" "}
              <a
                href={`${window.REACT_APP_STELLAR_URL}/${props.currentMember.blockchain_address}`}
                target="_blank"
                className={styles.link}
                rel="noopener noreferrer"
              >
                {i18n._("Link")}
              </a>
              <FontAwesomeIcon className={styles.icon} icon={faExternalLinkAlt} />
            </ListGroupItem>
          )}
        </ListGroup>
      </Container>
    </div>
  )
}

MemberOverview.propTypes = {
  currentMember: PropTypes.object,
  setPage: PropTypes.func,
  getBalance: PropTypes.func,
  vsla: PropTypes.object,
  memberBalance: PropTypes.func,
  memberDetails: PropTypes.object,
  isConnected: PropTypes.bool,
  group: PropTypes.object,
}

const txnTypes = {
  ar: {
    welfare: "مجموع مساهمات الرعاية",
    welfareContribution: " المساهمة في الرعاية",
    welfareContrib: "المساهمات في الرعاية",
    finePaid: "دفع الغرامة",
    "fine paid": "دفع الغرامة",
    fine: "الغرامات",
    membershipPaid: "دفع رسوم العضوية",
    shareBought: "إضافة المدخرات",
    loanWriteOff: "شطب القرض",
    shares: "المدخرات",
    loan: "",
    loan_interest_due: "",
    loanRepaid: "سداد القرض",
    LoanTaken: "الاستحصال على القرض",
    shareSold: "سحب المدخرات",
    welfareReq: "طلبات الرعاية",
    welfareRequest: "طلب الرعاية",
    membershipReversed: "إلغاء رسوم العضوية",
    fineReversed: "إلغاء غرامة",
    welfareRequestR: "إلغاء طلب الرعاية",
    otherIncome: "المداخيل الأخرى",
    loanRepayR: "إلغاء سداد القرض",
    sharesBoughtR: "إلغاء إضافة المدخرات",
    sharesSoldR: "إلغاء سحب المدخرات",
    writeoffReversed: "إلغاء شطب القرض",
    otherExpenses: "النفقات الأخرى",
    membershipcashout: "قبض رسوم العضوية",
    shareSoldCashOut: "قبض – سحب المدخرات",
    savingscashout: "قبض المدخرات",
    finecashout: "قبض غرامة",
    welfarecashout: "قبض الرعاية",
    chargeaccrued: " رسوم الخدمة المستحقة",
    loanBalance: "رصيد القرض",
    sharesBalance: "رصيد المدخرات",
    membership: "العضوية",
    fund_agri: "مساهمة الصندوق الزراعي",
  },
  lb: {
    welfareContribution: "Welfare contribution",
    welfareContrib: "Welfare contributions",
    finePaid: "Fine paid",
    "fine paid": "Fine paid",
    fine: "Fines",
    membershipPaid: "Membership paid",
    shareBought: "Savings added",
    loanWriteOff: "Loan write-off",
    shares: "Savings deposited",
    loan: "Unpaid Loan",
    loan_interest_due: "Unpaid loan interest",
    loanRepaid: "Loan repaid",
    LoanTaken: "Loan taken",
    shareSold: "Savings withdrawn",
    welfareReq: "Welfare requests",
    welfareRequest: "Welfare request",
    membershipReversed: "Membership fee reversed",
    fineReversed: "Fine reversed",
    welfareRequestR: "Welfare request reversed",
    otherIncome: "Group income",
    loanRepayR: "Loan repayment reversed",
    sharesBoughtR: "Add savings reversed",
    sharesSoldR: "Savings withdrawal reversed",
    writeoffReversed: "Loan write-off reversed",
    otherExpenses: "Group expenses",
    membershipcashout: "Membership cash out",
    shareSoldCashOut: "Savings withdrawal - cash out",
    savingscashout: "Savings cash out",
    finecashout: "Fine cash out",
    welfarecashout: "Welfare cash out",
    chargeaccrued: "Service charge accrued",
    loanBalance: "Loan balance",
    sharesBalance: "Savings balance",
    membership: "Membership",
    fund_agri: "Agricultural Fund Contribution",
  },
  pt: {
    welfareContribution: "Contribuição social",
    welfareContrib: "Contribuições sociais",
    welfare: "Contribuições totais de bem-estar",
    finePaid: "Multa paga",
    "fine paid": "Multa paga",
    fine: "Multas",
    membershipPaid: "Taxa de membro paga",
    shareBought: "Poupanças adicionadas",
    shares: "Poupanças adicionadas",
    loanWriteOff: "Empréstimo eliminado",
    loanRepaid: "Empréstimo pago",
    LoanTaken: "Empréstimo tomado",
    loan: "Empréstimo não pago",
    loan_interest_due: "Juros de empréstimo não pagos",
    shareSold: "Poupanças retiradas",
    welfareReq: "Pedidos sociais",
    welfare_requests: "Totais Pedido Social",
    welfareRequest: "Pedido social",
    membershipReversed: "Taxa de membro revertida",
    fineReversed: "Multa revertida",
    welfareRequestR: "Pedido social revertido",
    otherIncome: "Outros Rendimentos",
    loanRepayR: "Pagamento do empréstimo revertido",
    sharesBoughtR: "Adicionar poupanças revertidas",
    sharesSoldR: "Retirada de poupança revertida",
    writeoffReversed: "Eliminação de empréstimo revertida",
    otherExpenses: "Outras despesas",
    membershipcashout: "Saque de taxa de membros",
    shareSoldCashOut: "Saque de Retirada de poupança",
    savingscashout: "Saque de poupanças",
    finecashout: "Saque de Multas",
    welfarecashout: "Saque de fundo social",
    chargeaccrued: "Taxa de serviço acumulada",
    loanBalance: "Saldo do empréstimo",
    sharesBalance: "Saldo de poupanças",
    membership: "Filiação",
    savings: "Poupanças",
    fund_agri: "Contribuição do Fundo Agrícola",
  },
  en: {
    welfareContribution: "Welfare contribution",
    welfareContrib: "Welfare contributions",
    welfare: "Total welfare contributions",
    finePaid: "Fine paid",
    "fine paid": "Fine paid",
    fine: "Fines paid",
    shares: "Shares bought",
    membership: "Membership fee paid",
    membershipPaid: "Membership paid",
    shareBought: "Shares bought",
    savings: "Shares bought",
    loanWriteOff: "Loan write-off",
    loanRepaid: "Loan repaid",
    LoanTaken: "Loan taken",
    loan: "Unpaid Loan",
    loan_interest_due: "Unpaid loan interest",
    shareSold: "Shares sold",
    welfareReq: "Welfare requests",
    welfare_requests: "Total welfare requests",
    welfareRequest: "Welfare request",
    membershipReversed: "Membership reversed",
    fineReversed: "Fine reversed",
    welfareRequestR: "Welfare request reversed",
    otherIncome: "Group income",
    loanRepayR: "Loan repayment reversed",
    sharesBoughtR: "Shares bought reversed",
    sharesSoldR: "Shares sold reversed",
    writeoffReversed: "Write-off reversed",
    otherExpenses: "Group expenses",
    membershipcashout: "Membership cash out",
    shareSoldCashOut: "Shares sold - cash out",
    savingscashout: "Savings cash out",
    finecashout: "Fine cash out",
    welfarecashout: "Welfare cash out",
    chargeaccrued: "Service charge accrued",
    loanBalance: "Loan balance",
    sharesBalance: "Shares balance",
    fund_agri: "Agricultural Fund Contribution",
  },
  fr: {
    welfareContribution: "Welfare contribution",
    welfareContrib: "Welfare contributions",
    finePaid: "Fine paid",
    "fine paid": "Fine paid",
    fine: "Fines",
    welfare: "Total Welfare contributions",
    membershipPaid: "Membership paid",
    shareBought: "Shares bought",
    loanWriteOff: "Loan write-off",
    loanRepaid: "Loan repaid",
    LoanTaken: "Loan taken",
    shareSold: "Shares sold",
    welfareReq: "Welfare requests",
    welfareRequest: "Welfare request",
    membershipReversed: "Membership reversed",
    fineReversed: "Fine reversed",
    welfareRequestR: "Welfare request reversed",
    otherIncome: "Group income",
    loanRepayR: "Loan repayment reversed",
    sharesBoughtR: "Shares bought reversed",
    sharesSoldR: "Shares sold reversed",
    writeoffReversed: "Write-off reversed",
    otherExpenses: "Group expenses",
    membershipcashout: "Membership cash out",
    shareSoldCashOut: "Shares sold - cash out",
    savingscashout: "Savings cash out",
    finecashout: "Fine cash out",
    welfarecashout: "Welfare cash out",
    chargeaccrued: "Service charge accrued",
    loanBalance: "Loan balance",
    sharesBalance: "Shares balance",
    membership: "Membership",
    fund_agri: "Agricultural Fund Contribution",
  },
  sw: {
    welfareContribution: "Welfare contribution",
    welfareContrib: "Welfare contributions",
    finePaid: "Fine paid",
    "fine paid": "Fine paid",
    fine: "Fines",
    membershipPaid: "Membership paid",
    shareBought: "Shares bought",
    loanWriteOff: "Loan write-off",
    loanRepaid: "Loan repaid",
    LoanTaken: "Loan taken",
    shareSold: "Shares sold",
    welfareRequest: "Welfare request",
    welfareReq: "Welfare requests",
    membershipReversed: "Membership reversed",
    fineReversed: "Fine reversed",
    welfareRequestR: "Welfare request reversed",
    otherIncome: "Group income",
    loanRepayR: "Loan repayment reversed",
    sharesBoughtR: "Shares bought reversed",
    sharesSoldR: "Shares sold reversed",
    writeoffReversed: "Write-off reversed",
    otherExpenses: "Group expenses",
    membershipcashout: "Membership cash out",
    shareSoldCashOut: "Shares sold - cash out",
    savingscashout: "Savings cash out",
    finecashout: "Fine cash out",
    welfarecashout: "Welfare cash out",
    chargeaccrued: "Service charge accrued",
    loanBalance: "Loan balance",
    sharesBalance: "Shares balance",
    membership: "Membership",
    fund_agri: "Mchango wa Mkulima",
  },
  es: {
    welfareContribution: "Contribución al Fondo Social",
    welfareContrib: "Contribución al Fondo Social",
    finePaid: "Multa pagada",
    "fine paid": "Multa pagada",
    fine: "Multa",
    membershipPaid: "Membresía Pagada",
    shareBought: "Acciones compradas",
    loanWriteOff: "Cancelación de préstamos",
    loanRepaid: "Préstamo Reembolsado",
    LoanTaken: "Préstamo tomado",
    shareSold: "Acciones Vendidas",
    welfareRequest: "Solicitud de fondos de Fondo Social",
    welfareReq: "Solicitud de fondos de Fondo Social",
    membershipReversed: "Membresía Revertida",
    fineReversed: "Multa Revertida",
    welfareRequestR: "Solicitud de fondos de Fondo Social Revertido",
    otherIncome: "Otros ingresos",
    loanRepayR: "Reembolso del Préstamo Revertido",
    sharesBoughtR: "Acciones Compradas Revertidas",
    sharesSoldR: "Acciones Vendidas Revertidas",
    writeoffReversed: "Cancelación Revertida",
    otherExpenses: "Otros Gastos",
    membershipcashout: "Membresía - : retiro de efectivo",
    shareSoldCashOut: "Acciones Vendidas - retiro de efectivo",
    savingscashout: "Ahorros - retiro de efectivo",
    finecashout: "Multa - retiro de efectivo",
    welfarecashout: "Fondo Social - retiro de efectivo",
    chargeaccrued: "Cargo por servicio acumulado",
    loanBalance: "Saldo de Préstamo",
    sharesBalance: "Saldo de acciones",
    membership: "Membresía",
    fund_agri: "Contribución al Fondo Agrícola",
  },
}

const mapDispatchToProps = (dispatch) => ({
  getBalance: (id) => dispatch(getMemberBalanceByMemberId(id)),
  setPage: (page) => dispatch(setCurrentPage(page)),
})

const mapStateToProps = (state) => {
  const { account, accountV2, loadedMember, loadedvslaMembers } = state

  // This selector now uses the pubkey to find the correct account balance.
  const selectMemberBalance = (pubkey) => {
    if (!pubkey) return null

    // Priority 1: Check for an offline balance using the pubkey.
    if (accountV2.offline?.members[pubkey]) {
      return accountV2.offline.members[pubkey]
    }

    // Priority 3: Use the blockchain_wallet_id to look up the online balance.
    if (account.members[pubkey]) {
      return account.members[pubkey]
    }

    return null // No balance found.
  }

  return {
    isloading: state.vslaIsLoading,
    vsla: state.vsla,
    isConnected: state.isConnected,
    currentMember: loadedMember,
    loadedvslaMembers: loadedvslaMembers,
    group: state.group,
    error: state.vslaHasErrored,
    pendingTransactions: state.pendingTransactions,
    memberBalance: selectMemberBalance,
    memberDetails: loadedvslaMembers.find(
      ({ pubkey }) => pubkey === loadedMember?.pubkey,
    ),
    membersSaving: selectMemberSharesFund(state),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(MemberOverview)
